/**
 * @file paths.config.ts
 * @path apps/website/src/config/paths.config.ts
 * @description Centralized Navigation Engine.
 * Handles all internal path definitions, dynamic link generation, and UI navigation arrays.
 */

import {
  Briefcase,
  CalendarCheck,
  FileText,
  Home,
  Phone,
  Scale,
  ShieldCheck,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

// --- 1. PATH DEFINITIONS (Strings for Router) ---
export const PATHS = {
  ROOT: "/",
  EXPERIENCES: "/experiences",
  JOIN_US: "/join-us",
  CONTACT: "/enquiries",

  BOOKING: "/experiences/booking/:slug",
  CONFIRMATION: "/confirmation",

  // Dynamic Route Definitions
  EXPERIENCE_DETAILS: "/experiences/:slug",

  LEGAL: {
    PRIVACY: "/privacy-policy",
    TERMS: "/terms-of-service",
    LICENSE: "/license",
  },
} as const;

// --- 2. DYNAMIC LINK GENERATORS ---
export const makeLink = {
  experience: (slug: string) => `/${PATHS.EXPERIENCES}/${slug}`,
  service: (slug: string) => `/${PATHS.EXPERIENCES}/${slug}`,
  activity: (slug: string) => `/${PATHS.EXPERIENCES}/${slug}`,
  booking: (slug: string) => `/experiences/booking/${slug}`,
  confirmation: () => PATHS.CONFIRMATION,
};

// --- 3. NAV LINK OBJECT (Programmatic Access) ---
export const NAV_LINKS = {
  home: { path: PATHS.ROOT, label: "Home" },
  experiences: { path: PATHS.EXPERIENCES, label: "Experiences" },
  joinUs: { path: PATHS.JOIN_US, label: "Join Us" },
  contactUs: { path: PATHS.CONTACT, label: "Enquiries" },
  booking: { path: PATHS.BOOKING, label: "Booking Form" },
} as const;

// --- 4. UI NAVIGATION CONFIGURATION ---
export interface NavItem {
  name: string;
  icon: LucideIcon;
  path: string;
}

export const MAIN_NAV_ITEMS: NavItem[] = [
  { name: "Home", icon: Home, path: PATHS.ROOT },
  { name: "Experiences", icon: Briefcase, path: PATHS.EXPERIENCES },
  { name: "Join Us", icon: UserPlus, path: PATHS.JOIN_US },
  { name: "Enquiries", icon: Phone, path: PATHS.CONTACT },
];

export const LEGAL_NAV_ITEMS: NavItem[] = [
  { name: "Privacy Policy", icon: ShieldCheck, path: PATHS.LEGAL.PRIVACY },
  { name: "Terms of Service", icon: Scale, path: PATHS.LEGAL.TERMS },
  { name: "License", icon: FileText, path: PATHS.LEGAL.LICENSE },
];

export const QUICK_LINKS: NavItem[] = [
  { name: "Book a Shuttle", icon: CalendarCheck, path: PATHS.BOOKING },
  ...MAIN_NAV_ITEMS.slice(1, 3),
];
